import React from 'react'
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  SelectChangeEvent,
} from '@mui/material'
import { useSetupPipeline } from './hooks'
import { PromptSelect } from './PromptSelect'
import { ModelSelect } from './ModelSelect'

export function SetupPipeline() {
  const {
    processMode,
    fieldTitle,
    fieldDescription,
    fieldBodyValue,
    studentFacing,
    batchMode,
    setFieldTitle,
    setFieldDescription,
    setFieldBodyValue,
    setStudentFacing,
    handleProcessModeChange,
    handleBatchModeChange,
  } = useSetupPipeline()

  return (
    <Box border={1} borderColor="grey.300" borderRadius={1} p={2} mb={2}>
      <Typography variant="h6" gutterBottom sx={{ fontStyle: 'italic' }}>
        Setup
      </Typography>
      <Box mb={2}>
        <FormControl sx={{ minWidth: 250 }} size="small">
          <InputLabel>Mode</InputLabel>
          <Select
            value={processMode}
            onChange={(e: SelectChangeEvent) =>
              handleProcessModeChange(e.target.value as typeof processMode)
            }
            label="Mode"
          >
            <MenuItem value="ai-autocorrect">AI Autocorrect</MenuItem>
            <MenuItem value="title-autocorrect">
              Title Autocorrect (no AI)
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
      {processMode === 'ai-autocorrect' ? (
        <>
          <Box mb={2}>
            <FormControl sx={{ minWidth: 250 }} size="small">
              <InputLabel>Max Corrections Per Request</InputLabel>
              <Select
                value={String(batchMode)}
                onChange={(e: SelectChangeEvent) => {
                  handleBatchModeChange(
                    Number(e.target.value) as typeof batchMode,
                  )
                }}
                label="Max Corrections Per Request"
              >
                <MenuItem value="1">1 (Single)</MenuItem>
                <MenuItem value="5">5</MenuItem>
                <MenuItem value="10">10</MenuItem>
                <MenuItem value="50">50</MenuItem>
                <MenuItem value="100">100</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box mb={2}>
            <PromptSelect />
          </Box>
          <Box mb={2}>
            <ModelSelect />
          </Box>
          <Box mb={2}>
            <FormControl sx={{ minWidth: 250 }} size="small" variant="outlined">
              <Box
                sx={{
                  border: 1,
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                  borderRadius: 1,
                  p: 1.5,
                  position: 'relative',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                  }}
                >
                  Fields
                </Typography>
                <FormGroup sx={{ gap: 0 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={fieldTitle}
                        onChange={(e) => setFieldTitle(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Title"
                    sx={{ my: -0.5 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={fieldDescription}
                        onChange={(e) => setFieldDescription(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Description"
                    sx={{ my: -0.5 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={fieldBodyValue}
                        onChange={(e) => setFieldBodyValue(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Note Body Value"
                    sx={{ my: -0.5 }}
                  />
                </FormGroup>
              </Box>
            </FormControl>
          </Box>
          <Box mb={2}>
            <FormControl sx={{ minWidth: 250 }} size="small">
              <InputLabel>Student Facing</InputLabel>
              <Select
                value={studentFacing}
                onChange={(e: SelectChangeEvent) =>
                  setStudentFacing(e.target.value as typeof studentFacing)
                }
                label="Student Facing"
              >
                <MenuItem value="all">Any</MenuItem>
                <MenuItem value="true">True</MenuItem>
                <MenuItem value="false">False</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Titles will be autocorrected using Title Case (not using AI)
        </Typography>
      )}
    </Box>
  )
}
